/**
 * Japan Standard Time for admin-facing dates/times from the API.
 * Backend stores LocalDateTime as JST wall clock (often without a timezone suffix in JSON).
 */

export const JST_TIME_ZONE = 'Asia/Tokyo';

const BACKEND_LOCAL_DT =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;

/** yyyy/MM/dd HH:mm from backend local datetime string (already JST). */
export function formatBackendLocalDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const m = BACKEND_LOCAL_DT.exec(value.trim());
  if (!m) return value;
  return `${m[1]}/${m[2]}/${m[3]} ${m[4]}:${m[5]}`;
}

/** yyyy/MM/dd */
export function formatBackendLocalDate(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return value;
  return `${m[1]}/${m[2]}/${m[3]}`;
}

/** ISO instant or offset datetime → formatted in JST. */
export function formatInstantAsJst(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: JST_TIME_ZONE,
  }
): string {
  if (!value) return '—';
  const trimmed = value.trim();
  const local = BACKEND_LOCAL_DT.exec(trimmed);
  if (local) {
    return formatBackendLocalDateTime(trimmed);
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return trimmed;
  return d.toLocaleString('ja-JP', options);
}

/** Current calendar date in JST (yyyy-MM-dd). */
export function todayIsoDateJst(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: JST_TIME_ZONE });
}

/** Current clock time in JST for display. */
export function nowClockLabelJst(): { date: string; time: string } {
  const now = new Date();
  return {
    date: now.toLocaleDateString('ja-JP', { timeZone: JST_TIME_ZONE }),
    time: now.toLocaleTimeString('ja-JP', {
      timeZone: JST_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }),
  };
}
